import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { Container, OptionList, OptionStyled, SearchContainer } from './DropdownMenu.styled';
import { MagnifyingGlass } from 'assets/icons/Icons';
import { TextInput } from 'components/v2/Inputs/TextInput/TextInput';
import { escapeRegex } from 'kb-shared/utilities/regex';

import { Props, Option } from './DropdownMenu.types';

export const DropdownMenu = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    id,
    searchable,
    searchPlaceholder,
    onSelect,
    onClose,
    searchFn,
    selected,
    options,
    positionStatic,
    maxMenuHeight,
    initialFocusTarget = searchable ? 'search' : 'selectedOption'
  } = props;
  const isSelected = (option: Option): boolean => {
    return (
      selected?.some(
        selectedOption =>
          selectedOption.value === option.value && selectedOption.label === option.label
      ) ?? false
    );
  };

  const [value, setValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(null);
  const optionListRef = useRef<HTMLDivElement>(null);
  const searchInputId = `input-dropdown-search-${id}`;
  const optionId = (optionIndex: number) => `${id}-option-${optionIndex}`;

  const focusSearchInput = () => {
    const searchInput = document.getElementById(searchInputId) as HTMLInputElement | null;
    searchInput?.focus();
  };

  const focusOptionAtIndex = (optionIndex: number) => {
    if (filteredOptions.length === 0) return;

    const nextOptionIndex = Math.max(0, Math.min(optionIndex, filteredOptions.length - 1));
    setActiveOptionIndex(nextOptionIndex);

    const option = document.getElementById(optionId(nextOptionIndex));
    option?.focus();
  };

  const focusSelectedOptionOrFirst = () => {
    const selectedEl = optionListRef.current?.querySelector(
      '[aria-selected="true"]'
    ) as HTMLElement | null;
    const firstEl = optionListRef.current?.querySelector('[role="option"]') as HTMLElement | null;

    (selectedEl || firstEl)?.focus();
  };

  const scrollOptionIntoView = (optionIndex: number) => {
    const option = document.getElementById(optionId(optionIndex));
    if (option && 'scrollIntoView' in option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  };

  useEffect(() => {
    const focusFrame = requestAnimationFrame(() => {
      if (searchable && initialFocusTarget === 'search') {
        focusSearchInput();
        return;
      }

      focusSelectedOptionOrFirst();
    });

    return () => cancelAnimationFrame(focusFrame);
  }, [initialFocusTarget, searchable]);

  useEffect(() => {
    if (!searchable) return;

    if (filteredOptions.length === 0) {
      setActiveOptionIndex(null);
      return;
    }

    const selectedOptionIndex = filteredOptions.findIndex(option => isSelected(option));
    setActiveOptionIndex(selectedOptionIndex >= 0 ? selectedOptionIndex : null);
  }, [filteredOptions, searchable]);

  useEffect(() => {
    if (!searchable || activeOptionIndex === null) return;

    scrollOptionIntoView(activeOptionIndex);
  }, [activeOptionIndex, searchable]);

  const search = (term: string): void => {
    if (!term) return setFilteredOptions(options);

    if (searchFn) {
      const filteredByTerm = searchFn(options, term);
      setFilteredOptions(filteredByTerm);
    } else {
      const regex = new RegExp(`.*${escapeRegex(term)}.*`, 'i');
      const filteredByTerm = options.filter(option => regex.test(option.label));
      setFilteredOptions(filteredByTerm);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOptionAtIndex(activeOptionIndex ?? 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusOptionAtIndex(activeOptionIndex ?? filteredOptions.length - 1);
    } else if ((e.key === 'Enter' || e.key === ' ') && activeOptionIndex !== null) {
      e.preventDefault();
      onSelect(filteredOptions[activeOptionIndex]);
    } else if (e.key === 'Tab') {
      requestAnimationFrame(() => {
        onClose?.();
      });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.({ restoreFocus: true });
    }
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    option: Option,
    currentIndex: number
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOptionAtIndex(currentIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (currentIndex === 0 && searchable) {
        focusSearchInput();
        return;
      }

      focusOptionAtIndex(currentIndex - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(option);
    } else if (e.key === 'Tab') {
      requestAnimationFrame(() => {
        onClose?.();
      });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.({ restoreFocus: true });
    }
  };

  return (
    <Container ref={ref} $positionStatic={positionStatic} $maxMenuHeight={maxMenuHeight}>
      {searchable && (
        <SearchContainer>
          <TextInput
            id={searchInputId}
            leftIcon={<MagnifyingGlass type="solid" />}
            type="search"
            placeholder={searchPlaceholder}
            value={value}
            ariaLabel={searchPlaceholder || 'Search options'}
            ariaControls={id}
            onKeyDown={handleSearchKeyDown}
            onChange={e => {
              setValue(e.currentTarget.value);
              search(e.currentTarget.value);
            }}
          />
        </SearchContainer>
      )}

      <OptionList ref={optionListRef} id={id} role="listbox">
        {filteredOptions.map((option, index) => (
          <OptionStyled
            onClick={() => onSelect(option)}
            onKeyDown={e => handleOptionKeyDown(e, option, index)}
            onFocus={() => {
              if (searchable) {
                setActiveOptionIndex(index);
              }
            }}
            onMouseEnter={() => {
              if (searchable) {
                setActiveOptionIndex(index);
              }
            }}
            id={optionId(index)}
            key={`${option.value} ${option.label}`}
            aria-selected={isSelected(option)}
            $isActive={searchable && activeOptionIndex === index}
            tabIndex={-1}
          >
            {option.label}
          </OptionStyled>
        ))}
      </OptionList>
    </Container>
  );
});

// must be set because of forwardRef otherwise eslint complains
DropdownMenu.displayName = 'DropdownMenu';
